import React, {
  createContext,
  useContext,
  useReducer,
  useEffect
} from "react";

const CartContext = createContext();

// ---------- Initial State ----------
const initialState = {
  items: [],
  total: 0
};

// ---------- Reducer ----------
const cartReducer = (state, action) => {

  switch (action.type) {

    case "SET_CART":
      return action.payload;

    case "ADD_TO_CART": {

      const product = action.payload;

      const existingItem = state.items.find(
        (item) =>
          item._id === product._id &&
          item.selectedSize === product.selectedSize &&
          item.selectedColor === product.selectedColor
      );

      let updatedItems;

      if (existingItem) {

        updatedItems = state.items.map((item) =>
          item._id === product._id &&
          item.selectedSize === product.selectedSize &&
          item.selectedColor === product.selectedColor
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );

      } else {

        updatedItems = [
          ...state.items,
          { ...product, quantity: 1 }
        ];

      }

      return {
        ...state,
        items: updatedItems
      };
    }

    case "UPDATE_QUANTITY": {

      const updatedItems = state.items.map((item) =>
        item._id === action.payload.id
          ? {
              ...item,
              quantity: Math.max(1, action.payload.quantity)
            }
          : item
      );

      return {
        ...state,
        items: updatedItems
      };
    }

    case "REMOVE_FROM_CART":

      return {
        ...state,
        items: state.items.filter(
          (item) => item._id !== action.payload
        )
      };

    case "CLEAR_CART":

      return {
        items: [],
        total: 0
      };

    case "SET_TOTAL":

      return {
        ...state,
        total: action.payload
      };

    default:
      return state;
  }
};

// ---------- Provider ----------
export const CartProvider = ({ children }) => {

  const [state, dispatch] = useReducer(
    cartReducer,
    initialState
  );

  // ---------- Restore cart ----------
  useEffect(() => {

    try {

      const savedCart = localStorage.getItem("cart");

      if (savedCart) {

        const parsedCart = JSON.parse(savedCart);

        dispatch({
          type: "SET_CART",
          payload: parsedCart
        });

      }

    } catch (error) {

      console.error("Cart restore failed", error);

    }

  }, []);

  // ---------- Calculate total ----------
  useEffect(() => {

    const total = state.items.reduce(
      (sum, item) =>
        sum + item.price * item.quantity,
      0
    );

    if (total !== state.total) {

      dispatch({
        type: "SET_TOTAL",
        payload: total
      });

    }

  }, [state.items]);

  // ---------- Save cart ----------
  useEffect(() => {

    localStorage.setItem(
      "cart",
      JSON.stringify(state)
    );

  }, [state]);

  // ---------- Actions ----------

  const addToCart = (product, size, color) => {

    dispatch({
      type: "ADD_TO_CART",
      payload: {
        ...product,
        selectedSize: size,
        selectedColor: color
      }
    });

  };

  const updateQuantity = (id, quantity) => {

    dispatch({
      type: "UPDATE_QUANTITY",
      payload: { id, quantity }
    });

  };

  const removeFromCart = (id) => {

    dispatch({
      type: "REMOVE_FROM_CART",
      payload: id
    });

  };

  const clearCart = () => {

    dispatch({ type: "CLEAR_CART" });

  };

  // ---------- Derived values ----------

  const cartCount = state.items.reduce(
    (count, item) => count + item.quantity,
    0
  );

  return (

    <CartContext.Provider
      value={{
        items: state.items,
        total: state.total,
        cartCount,
        addToCart,
        updateQuantity,
        removeFromCart,
        clearCart
      }}
    >

      {children}

    </CartContext.Provider>

  );

};

// ---------- Hook ----------
export const useCart = () => {

  const context = useContext(CartContext);

  if (!context) {
    throw new Error("useCart must be used inside CartProvider");
  }

  return context;

};